import httpClient, { TimeoutError } from "../../utils/httpClient";
import {
  BonusApplication,
  BonusApplicationDetails,
  BonusQueryParams,
  EditBonusApplicationPayload,
  BonusApplicationApiResponse,
  Project, // Added Project type
  BonusDetailsResponse,
  Employee,
  GetEmployeesParams,
  GetOverrideEmployeesParams,
  DEFAULT_JOB_FAMILIES,
  GetOverrideEmployeesResponse,
  GetEmployeesResponse,
} from "./types";

// Add this enum to your types
enum RoleType {
  None = 0,
  SPM = 1,
  DCCM = 2,
  DM = 3,
  VP = 4,
  Ops = 5,
  Payroll = 6,
}

interface BulkUpdatePayload {
  comments: string[];
  applications: EditBonusApplicationPayload[];
}

export const bonusApi = {
  // Get bonus application details by ID
  getBonusApplicationDetails: async (bonusApplicationId: number) => {
    const response = await httpClient.get<BonusDetailsResponse>(
      `/api/bonusApplication/${bonusApplicationId}`
    );
    return response.data;
  },

  // Get bonus applications with query params
  getBonusApplications: async (
    params: BonusQueryParams
  ): Promise<BonusApplicationApiResponse> => {
    try {
      const response = await httpClient.get("/api/bonusapplications", {
        params,
      });

      // Check if response.data is the error string
      if (
        typeof response.data === "string" &&
        response.data === "Unexpected error during GetBonusApplications"
      ) {
        console.error("Error fetching bonus applications:", response.data);
        return {
          data: [],
          error: "Unable to load bonus applications. Please try again later.",
        };
      } else if (
        typeof response.data === "string" &&
        response.data ===
        "Unexpected error during GetBonusApplications: UnauthorizedAccessException"
      ) {
        console.error("Error fetching bonus applications:", response.data);
        return {
          data: [],
          error:
            "User is not authorized to access bonus application. Please check with QBA team for access.",
        };
      }

      return { data: response.data };
    } catch (error) {
      console.error("Error fetching bonus applications:", error);
      return {
        data: [],
        error:
          "Failed to fetch bonus applications. Please check your connection acees to QBA application, and try again.",
      };
    }
  },

  // Get action items
  getMyActionItems: async (
    params: BonusQueryParams
  ): Promise<BonusApplicationApiResponse> => {
    try {
      const response = await httpClient.get("/api/user-action-items", {
        params,
      });

      // Check if response.data is the error string
      if (
        typeof response.data === "string" &&
        response.data === "Unexpected error during GetBonusApplications"
      ) {
        console.error("Error fetching bonus applications:", response.data);
        return {
          data: [],
          error: "Unable to load bonus applications. Please try again later.",
        };
      } else if (
        typeof response.data === "string" &&
        response.data ===
        "Unexpected error during GetBonusApplications: UnauthorizedAccessException"
      ) {
        console.error("Error fetching bonus applications:", response.data);
        return {
          data: [],
          error:
            "User is not authorized to access bonus application. Please check with QBA team for access.",
        };
      }

      return { data: response.data };
    } catch (error) {
      console.error("Error fetching bonus applications:", error);
      return {
        data: [],
        error:
          "Failed to fetch bonus applications. Please check your connection acees to QBA application, and try again.",
      };
    }
  },

  // Get user reviews
  getUserReviews: async (
    params: BonusQueryParams
  ): Promise<BonusApplicationApiResponse> => {
    try {
      const response = await httpClient.get("/api/user-reviews", { params });

      // Check if response.data is the error string
      if (
        typeof response.data === "string" &&
        response.data === "Unexpected error during GetBonusApplications"
      ) {
        console.error("Error fetching bonus applications:", response.data);
        return {
          data: [],
          error: "Unable to load bonus applications. Please try again later.",
        };
      } else if (
        typeof response.data === "string" &&
        response.data ===
        "Unexpected error during GetBonusApplications: UnauthorizedAccessException"
      ) {
        console.error("Error fetching bonus applications:", response.data);
        return {
          data: [],
          error:
            "User is not authorized to access bonus application. Please check with QBA team for access.",
        };
      }

      return { data: response.data };
    } catch (error) {
      console.error("Error fetching bonus applications:", error);
      return {
        data: [],
        error:
          "Failed to fetch bonus applications. Please check your connection acees to QBA application, and try again.",
      };
    }
  },

  // Get my submissions
  getMySubmissions: async (
    params: BonusQueryParams
  ): Promise<BonusApplicationApiResponse> => {
    try {
      const response = await httpClient.get("/api/my-submissions", { params });
      if (
        typeof response.data === "string" &&
        response.data.startsWith("Unexpected error during GetMySubmissions")
      ) {
        return {
          data: [],
          error: "Unable to load my submissions. Please try again later.",
        };
      }
      return { data: response.data };
    } catch (error) {
      console.error("Error fetching my submissions:", error);
      return {
        data: [],
        error: "Failed to fetch my submissions. Please check your connection and try again.",
      };
    }
  },

  // Edit bonus application
  editBonusApplication: async (data: EditBonusApplicationPayload) => {
    const response = await httpClient.put(
      `/api/bonusapplication/${data.BonusApplicationId}`,
      {
        UserAction: data.UserAction,
        Amount: data.Amount || 0,
        BonusSheet: data.BonusSheet,
        Comments: data.Comments || [],
      }
    );
    return response.data;
  },

  // Bulk update bonus applications
  bulkUpdateBonusApplications: async (data: { comments: string[], applications: EditBonusApplicationPayload[] }) => {
    const payload = {
      applications: data.applications.map((app) => ({
        bonusApplicationId: app.BonusApplicationId,
        userAction: app.UserAction,
        amount: app.Amount || 0,
        bonusSheet: app.BonusSheet,
        exEmployee: app.ExEmployee || null,
      })),
      comments: data.comments || [],
    };

    const response = await httpClient.put(
      "/api/bonusapplication/bulk",
      payload
    );
    return response.data;
  },

  updateBonusApplication: async (bonusApplicationId: number, payload: EditBonusApplicationPayload) => {
    try {
      const response = await httpClient.put(`/api/bonus/${bonusApplicationId}`, payload);
      return response.data;
    } catch (error) {
      if (error instanceof TimeoutError) {
        console.error(`Request timed out for ${error.config.method.toUpperCase()} ${error.config.url} — ${error.message}. Please check if the bonus application was updated before trying again`);
      } else {
        console.error('Error updating bonus application:', error);
      }
      throw error;
    }
  },

  // Get templates by role
  getTemplates: async (roleType: string) => {
    try {
      const response = await httpClient.get("/api/templates", {
        headers: { roleType },
      });
      return response.data;
    } catch (error) {
      if (error instanceof TimeoutError) {
        console.error(`Request timed out for ${error.config.method.toUpperCase()} ${error.config.url} — ${error.message}. Please check if the data loaded before trying again`);
      } else {
        console.error('Error fetching templates:', error);
      }
      throw error;
    }
  },

  // Get JSON templates
  getJsonTemplates: async (roleType: string) => {
    try {
      const response = await httpClient.get("/api/jsontemplates", {
        headers: { roleType },
      });
      return response.data;
    } catch (error) {
      if (error instanceof TimeoutError) {
        console.error(`Request timed out for ${error.config.method.toUpperCase()} ${error.config.url} — ${error.message}. Please check if the data loaded before trying again`);
      } else {
        console.error('Error fetching JSON templates:', error);
      }
      throw error;
    }
  },

  // Get projects
  getProjects: async (): Promise<Project[]> => {
    const localStorageKey = "projects";
    const expiryDuration = 24 * 60 * 60 * 1000; // 24 hours in milliseconds
    const currentTime = Date.now();

    // Check if projects are already in localStorage and not expired
    const storedData = localStorage.getItem(localStorageKey);
    if (storedData) {
      const [storedExpiry, storedProjects] = storedData.split(" | ");
      if (Number(storedExpiry) > currentTime) {
        return JSON.parse(atob(storedProjects)) as Project[];
      }
    }

    try {
      // Fetch projects from API if not in localStorage or expired
      const response = await httpClient.get<Project[]>("/api/projects");
      // Encode projects to Base64 before storing in localStorage
      const projects = response.data;
      const encodedProjects = btoa(JSON.stringify(projects));

      // Store the fetched projects and expiry in localStorage
      const newExpiry = currentTime + expiryDuration;
      localStorage.setItem(
        localStorageKey,
        `${newExpiry} | ${encodedProjects}`
      );

      return projects;
    } catch (error) {
      if (error instanceof TimeoutError) {
        console.error(`Request timed out for ${error.config.method.toUpperCase()} ${error.config.url} — ${error.message}. Please check if the projects loaded before trying again`);
      } else {
        console.error('Error fetching projects:', error);
      }
      throw error;
    }
  },

  // Create bonus application for different types (CCM1, CCM2, CCM3, CM3, PM, DCCM, SPM)
  createBonusApplication: async (data: BonusApplication) => {
    try {
      const response = await httpClient.post("/api/bonusapplication", data);
      return response.data;
    } catch (error) {
      if (error instanceof TimeoutError) {
        console.error(`Request timed out for ${error.config.method.toUpperCase()} ${error.config.url} — ${error.message}. Please check if the bonus application was created before trying again`);
      } else {
        console.error('Error creating bonus application:', error);
      }
      throw error;
    }
  },
  // ==================== File Attachment APIs ====================

  // Get file attachments by email
  getFileAttachmentsByEmail: async (email: string) => {
    try {
      const response = await httpClient.get(
        `/api/file-attachments/email/${email}`
      );
      console.log("File attachments by email response:", response.data);
      return response.data;
    } catch (error) {
      if (error instanceof TimeoutError) {
        console.error(`Request timed out for ${error.config.method.toUpperCase()} ${error.config.url} — ${error.message}. Please check if the data loaded before trying again`);
      } else {
        console.error('Error fetching file attachments by email:', error);
      }
      throw error;
    }
  },

  // Get file attachments by employee ID
  getFileAttachmentsByEmployee: async (employeeId: number) => {
    try {
      const response = await httpClient.get(
        `/api/file-attachments/employee/${employeeId}`
      );
      return response.data;
    } catch (error) {
      if (error instanceof TimeoutError) {
        console.error(`Request timed out for ${error.config.method.toUpperCase()} ${error.config.url} — ${error.message}. Please check if the data loaded before trying again`);
      } else {
        console.error('Error fetching file attachments by employee:', error);
      }
      throw error;
    }
  },

  // Get file attachments by quarter
  getFileAttachmentsByQuarter: async (quarter: string) => {
    try {
      const response = await httpClient.get(
        `/api/file-attachments/quarter/${quarter}`
      );
      return response.data;
    } catch (error) {
      if (error instanceof TimeoutError) {
        console.error(`Request timed out for ${error.config.method.toUpperCase()} ${error.config.url} — ${error.message}. Please check if the data loaded before trying again`);
      } else {
        console.error('Error fetching file attachments by quarter:', error);
      }
      throw error;
    }
  },

  // Download file by ID
  downloadAttachmentFile: async (fileId: number) => {
    try {
      const response = await httpClient.get(`/api/attachmentfile/${fileId}`, {
        responseType: "blob",
      });
      return response.data;
    } catch (error) {
      if (error instanceof TimeoutError) {
        console.error(`Request timed out for ${error.config.method.toUpperCase()} ${error.config.url} — ${error.message}. Please check if the file was downloaded before trying again`);
      } else {
        console.error('Error downloading attachment file:', error);
      }
      throw error;
    }
  },

  // Delete file attachment
  deleteFileAttachment: async (fileId: number) => {
    try {
      await httpClient.delete(`/api/file-attachments/${fileId}`);
    } catch (error) {
      if (error instanceof TimeoutError) {
        console.error(`Request timed out for ${error.config.method.toUpperCase()} ${error.config.url} — ${error.message}. Please check if the file was deleted before trying again`);
      } else {
        console.error('Error deleting file attachment:', error);
      }
      throw error;
    }
  },

  getEmployees: async (params: GetEmployeesParams): Promise<Employee[]> => {
    try {
      const response = await httpClient.get<GetEmployeesResponse>('/api/employees');

      const raw: any[] = response.data?.employees ?? response.data ?? [];

      return raw.map(e => ({
        EmployeeNumber: e.EmployeeNumber ?? e.employeeNumber ?? '',
        EmployeeName: e.EmployeeName ?? e.employeeName ?? '',
        EmployeeEmail: e.EmployeeEmail ?? e.employeeEmail ?? '',
        ApplicationType: e.ApplicationType ?? e.applicationType ?? null,
        JobTitle: e.JobTitle ?? e.jobTitle ?? '',
        JobFamily: e.JobFamily ?? e.jobFamily ?? '',
        SortOrder: e.SortOrder ?? e.sortOrder ?? 0,
        EmploymentStatus: e.EmploymentStatus ?? e.employmentStatus ?? '',
        SupervisorName: e.SupervisorName ?? e.supervisorName ?? '',
        SupervisorEmail: e.SupervisorEmail ?? e.supervisorEmail ?? '',
        HasFoundationCalls: e.HasFoundationCalls ?? e.hasFoundationCalls ?? false,
      }));
    } catch (error) {
      if (error instanceof TimeoutError) {
        console.error(`Request timed out for ${error.config.method.toUpperCase()} ${error.config.url} — ${error.message}. Please check if the data loaded before trying again`);
      } else {
        console.error('Error fetching employees:', error);
      }
      return [];
    }
  },

  getOverrideEmployees: async (params: GetOverrideEmployeesParams = {}): Promise<GetOverrideEmployeesResponse> => {
    try {
      const jobFamily = params.jobFamily ?? DEFAULT_JOB_FAMILIES;
      const queryParams = new URLSearchParams({ jobFamily });

      const response = await httpClient.get<GetOverrideEmployeesResponse>(`/api/employees/override?${queryParams.toString()}`);

      const raw: any[] = response.data?.employees ?? [];

      return {
        employees: raw.map(e => ({
          EmployeeNumber: e.EmployeeNumber ?? e.employeeNumber ?? '',
          EmployeeName: e.EmployeeName ?? e.employeeName ?? '',
          EmployeeEmail: e.EmployeeEmail ?? e.employeeEmail ?? '',
          ApplicationType: e.ApplicationType ?? e.applicationType ?? null,
          JobTitle: e.JobTitle ?? e.jobTitle ?? '',
          JobFamily: e.JobFamily ?? e.jobFamily ?? '',
          SortOrder: e.SortOrder ?? e.sortOrder ?? 0,
          EmploymentStatus: e.EmploymentStatus ?? e.employmentStatus ?? '',
          SupervisorName: e.SupervisorName ?? e.supervisorName ?? '',
          SupervisorEmail: e.SupervisorEmail ?? e.supervisorEmail ?? '',
          HasFoundationCalls: e.HasFoundationCalls ?? e.hasFoundationCalls ?? false,
        })),
      };
    } catch (error) {
      if (error instanceof TimeoutError) {
        console.error(`Request timed out for ${error.config.method.toUpperCase()} ${error.config.url} — ${error.message}. Please check if the data loaded before trying again`);
      } else {
        console.error('Error fetching override employees:', error);
      }
      return { employees: [] };
    }
  }

};
